import { useCallback } from 'react'
import { Interface } from '@ethersproject/abi'
import { useStakeContract, useStakeWriteContract } from 'hooks/useContract'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'
import { logMessage } from 'utils/logMessage'
import { StakeClaimReward, StakeReward } from 'views/Stake/types'
import useConfirmTransactionModal from 'components/widgets/ConfirmTransactionModal/useConfirmTransactionModal'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'
import useToast from 'hooks/useToast'
import { useTranslation } from 'react-i18next'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { BigNumber, ethers } from 'ethers'
import { useGetMproPrice } from 'hooks/useGetMproPrice'
import { BASE_CHAIN_ID_NUMBER, BNToNumberWithDecimals, BNToString, stringToBigNumber } from '@mprolab/sdk'
import { getMulticallResponse } from 'utils/getMulticallResponse'
import { MproRewardStake, MproDoubleRewardAutoStake } from 'config/abi/types'

export interface StakerAutostake {
  staked: BigNumber
  reward: BigNumber
  rewardDebt: BigNumber
  balanceWithRewards: BigNumber
  claimedBalance: BigNumber
}

export interface StakerDualAutostake {
  staked: BigNumber
  reward: BigNumber
  rewardDebt: BigNumber
  claimedReward: BigNumber
  claimedStaked: BigNumber
}

const isStakerDualAutostake = (staker: StakerAutostake | StakerDualAutostake): staker is StakerDualAutostake => {
  return 'claimedReward' in staker && 'claimedStaked' in staker
}

const isDualAutostake = (
  stakeContract: MproRewardStake | MproDoubleRewardAutoStake
): stakeContract is MproDoubleRewardAutoStake => {
  return typeof (stakeContract as any).claimReward === 'function'
}

const SECONDS_IN_YEAR = 365 * 24 * 60 * 60

export const useMproStakeRewardContract = (
  address: string
): {
  getConfigWithAccount: (account: string) => Promise<{
    stakeConfig: StakeReward
    claimConfig: StakeClaimReward
    rewardConfig?: { currentToClaim: BigNumber }
  }>
  getConfigWithoutAccount: () => Promise<{ stakeConfig: StakeReward; claimConfig: StakeClaimReward }>
  claimStake: () => Promise<void>
  claimReward: () => Promise<void>
  getWalletStakeUpdates: (account: string) => Promise<any>
  getDeclarationEndTimestamp: () => Promise<BigNumber>
  getDeclarationStartTimestamp: () => Promise<BigNumber>
  getStakeStartTimestamp: () => Promise<BigNumber>
  getStakeEndTimestamp: () => Promise<BigNumber>
  getStakeDuration: () => Promise<BigNumber>
  getUpdateStakersEndTimestamp: () => Promise<BigNumber>
  getTotalStakedSupply: () => Promise<BigNumber>
} => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const { onPresentConfirmTransactionModal, onDismissConfirmTransactionModal } = useConfirmTransactionModal()
  const StakeContract = useStakeContract(address)
  const StakeWriteContract = useStakeWriteContract(address)
  const { toastInfo, toastSuccess, toastError } = useToast()
  const { getMproPrice } = useGetMproPrice()
  const ensureChainForTx = useSwitchChainForContractTx()

  const getTotalStakedSupply = async () => {
    try {
      return await StakeContract.totalStakedSupply()
    } catch (error) {
      logMessage(`Failed to fetch total staked supply: ${error}`)
      return null
    }
  }

  const getRewardStakeChainId = async (): Promise<number> => {
    try {
      const network = await StakeContract.provider.getNetwork()
      return network.chainId
    } catch (error) {
      logMessage(`Failed to fetch total staked supply: ${error}`)
      return null
    }
  }

  const getStakerInfo = useCallback(
    async (address: string) => {
      try {
        return await StakeContract.staker(address)
      } catch (error) {
        logMessage(`Failed to fetch staker info: ${error}`)
        return null
      }
    },
    [StakeContract, account]
  )

  const getPendingReward = useCallback(
    async (address: string) => {
      try {
        return await StakeContract.pendingReward(address)
      } catch (error) {
        logMessage(`Failed to fetch pending reward: ${error}`)
        return null
      }
    },
    [StakeContract, account]
  )

  const getEnableForRelease = async () => {
    try {
      return StakeContract.enableForRelease()
    } catch (error) {
      logMessage(`Failed to fetch current to claim: ${error}`)
      return null
    }
  }

  const getNextReleaseAllocation = async () => {
    try {
      return await StakeContract.nextReleaseAllocation()
    } catch (error) {
      logMessage(`Failed to fetch next release allocation: ${error}`)
      return null
    }
  }

  const getWalletStakeUpdates = useCallback(
    async (account: string) => {
      try {
        return await StakeContract.getWalletStakeUpdates(account)
      } catch (error) {
        logMessage(`Failed to fetch wallet stake updates: ${error}`)
        return null
      }
    },
    [StakeContract, account]
  )

  const getDeclarationEndTimestamp = async () => {
    try {
      return await StakeContract.declarationEndTimestamp()
    } catch (error) {
      logMessage(`Failed to fetch declaration end timestamp: ${error}`)
      return null
    }
  }

  const getDeclarationStartTimestamp = async () => {
    try {
      return await StakeContract.declarationStartTimestamp()
    } catch (error) {
      logMessage(`Failed to fetch declaration start timestamp: ${error}`)
      return null
    }
  }

  const getStakeStartTimestamp = async () => {
    try {
      return await StakeContract.stakeStartTimestamp()
    } catch (error) {
      logMessage(`Failed to fetch stake start timestamp: ${error}`)
      return null
    }
  }

  const getStakeEndTimestamp = async () => {
    try {
      return await StakeContract.stakeEndTimestamp()
    } catch (error) {
      logMessage(`Failed to fetch stake end timestamp: ${error}`)
      return null
    }
  }

  const getStakeDuration = async () => {
    try {
      return await StakeContract.stakeDuration()
    } catch (error) {
      logMessage(`Failed to fetch stake duration: ${error}`)
      return null
    }
  }

  const getStakeNextReleaseTimestamp = async () => {
    try {
      return await StakeContract.nextReleaseTimestamp()
    } catch (error) {
      logMessage(`Failed to fetch next release timestamp: ${error}`)
      return null
    }
  }

  const getUpdateStakersEndTimestamp = async () => {
    try {
      return await StakeContract.updateStakersEndTimestamp()
    } catch (error) {
      logMessage(`Failed to fetch update stakers end timestamp: ${error}`)
      return null
    }
  }

  const getEarnedAmount = useCallback(
    async (address: string) => {
      try {
        return await StakeContract.getEarnedAmount(address)
      } catch (error) {
        logMessage(`Failed to fetch earned amount: ${error}`)
        return null
      }
    },
    [StakeContract, account]
  )

  const claimStake = async () => {
    try {
      const stakeChainId = await getRewardStakeChainId()
      if (stakeChainId == null) {
        // Not "try again": we could not read which chain this stake settles on,
        // and pressing the button a second time reads the same silence.
        toastError(t('stakingPanel.claimFailed'), t('globalErrors.stakeChainUnknown'))
        return
      }
      const onCorrectChain = await ensureChainForTx(stakeChainId)
      if (!onCorrectChain) {
        /*
         * Three ways to get here and one honest sentence: the wallet refused
         * the switch, it cannot switch on its own, or it did not answer at all
         * because its session is gone. All three mean the same thing to the
         * person holding the phone — open the wallet and check it.
         */
        toastError(t('stakingPanel.claimFailed'), t('globalErrors.walletNotReadyForTx'))
        return
      }
      onPresentConfirmTransactionModal()
      const claimFunction = await StakeWriteContract.claim()
      onDismissConfirmTransactionModal()
      toastInfo(t('transactionPending'))
      await claimFunction.wait()
      toastSuccess(t('stakingPanel.claimSuccess'))
    } catch (error) {
      onDismissConfirmTransactionModal()
      /*
       * The contract said something specific and the user was told "try
       * again" — sixteen times across eight wallets between 1 and 6 September,
       * and every one of those people had an answer waiting in the revert:
       * the release window not open, the stake ended, the wallet refusing to
       * switch, no gas. The decoder already existed; this path simply never
       * called it.
       */
      toastError(t('stakingPanel.claimFailed'), getErrorCodesDescription(t, error))
      logMessage(error)
    }
  }

  const claimReward = async () => {
    if (!isDualAutostake(StakeContract)) {
      return
    }
    try {
      const stakeChainId = await getRewardStakeChainId()
      if (stakeChainId == null) {
        toastError(t('stakingPanel.claimFailed'), t('globalErrors.stakeChainUnknown'))
        return
      }
      const onCorrectChain = await ensureChainForTx(stakeChainId)
      if (!onCorrectChain) {
        /*
         * Three ways to get here and one honest sentence: the wallet refused
         * the switch, it cannot switch on its own, or it did not answer at all
         * because its session is gone. All three mean the same thing to the
         * person holding the phone — open the wallet and check it.
         */
        toastError(t('stakingPanel.claimFailed'), t('globalErrors.walletNotReadyForTx'))
        return
      }
      // Only MproDoubleRewardAutoStake exposes claimReward; MproRewardStake does
      // not. Narrowing here is both the type fix and a real guard — calling it
      // on the other contract would have thrown "not a function" at the user.
      if (!('claimReward' in StakeWriteContract)) {
        toastError(t('stakingPanel.claimFailed'), t('globalErrors.noSeparateReward'))
        return
      }
      onPresentConfirmTransactionModal()
      const claimFunction = await StakeWriteContract.claimReward()
      onDismissConfirmTransactionModal()
      toastInfo(t('transactionPending'))
      await claimFunction.wait()
      toastSuccess(t('stakingPanel.claimSuccess'))
    } catch (error) {
      onDismissConfirmTransactionModal()
      /*
       * The contract said something specific and the user was told "try
       * again" — sixteen times across eight wallets between 1 and 6 September,
       * and every one of those people had an answer waiting in the revert:
       * the release window not open, the stake ended, the wallet refusing to
       * switch, no gas. The decoder already existed; this path simply never
       * called it.
       */
      toastError(t('stakingPanel.claimFailed'), getErrorCodesDescription(t, error))
      logMessage(error)
    }
  }

  const getRewardAmount = async () => {
    if (!isDualAutostake(StakeContract)) {
      throw new Error('Stake contract is not dual')
    }
    try {
      return await StakeContract.getRewardAmount()
    } catch (error) {
      logMessage(error)
      return null
    }
  }

  const getRewardPerSecond = async () => {
    try {
      return await StakeContract.rewardPerSecond()
    } catch (error) {
      logMessage(`Failed to fetch reward per second: ${error}`)
      return null
    }
  }

  const getAPYCalculation = async (): Promise<BigNumber> => {
    try {
      const rewardPerSecond = await getRewardPerSecond()
      let totalStakedSupply = await getTotalStakedSupply()

      if (totalStakedSupply.eq(0)) {
        totalStakedSupply = ethers.utils.parseUnits('1', 18)
      }

      const ONE_MPRO = BigNumber.from(1).mul(BigNumber.from(10).pow(BigNumber.from(18)))
      // TODO: For now we will use only BASE_CHAIN_ID_NUMBER for price fetching due to MPRO price fluctuations on ETH
      const actualPrice = await getMproPrice(ONE_MPRO, BASE_CHAIN_ID_NUMBER, false)

      const actualPriceBigNumber = stringToBigNumber(actualPrice.toString())

      const rewardPerYearValue = rewardPerSecond
        .mul(BigNumber.from(SECONDS_IN_YEAR))
        .mul(actualPriceBigNumber)
        .div(BigNumber.from(10).pow(18))
      const totalStakedSupplyValue = totalStakedSupply.mul(actualPriceBigNumber).div(BigNumber.from(10).pow(18))

      const apy = BigNumber.from(
        ((BNToNumberWithDecimals(rewardPerYearValue) / BNToNumberWithDecimals(totalStakedSupplyValue)) * 100).toFixed()
      )
      return apy
    } catch (error) {
      logMessage(`Failed to calculate APY: ${error}`)
      return null
    }
  }

  const getConfigWithAccount = async (
    account: string
  ): Promise<{
    stakeConfig: StakeReward
    claimConfig: StakeClaimReward
    rewardConfig?: { currentToClaim: BigNumber }
  }> => {
    let stakerInfo: StakerAutostake | StakerDualAutostake
    let pendingReward: BigNumber
    let earnedAmount: BigNumber
    let totalToClaim: BigNumber | null = null

    if (account) {
      stakerInfo = await getStakerInfo(account)
      pendingReward = await getPendingReward(account)
      earnedAmount = await getEarnedAmount(account)
    }
    if (earnedAmount && pendingReward) {
      earnedAmount = earnedAmount.add(pendingReward)
    }

    if (stakerInfo) {
      if (isStakerDualAutostake(stakerInfo)) {
        totalToClaim = stakerInfo.staked.sub(stakerInfo.claimedStaked)
      } else {
        totalToClaim = stakerInfo.balanceWithRewards.sub(stakerInfo.claimedBalance)
      }
    }

    const stakeNetwork = await StakeContract.provider.getNetwork()

    const [
      totalStakedSupplyRes,
      nextReleaseTimestampRes,
      claimRewardStartTimestampRes,
      claimPeriodDurationRes,
      rewardUnlockPercentPerPeriodRes
    ] = await getMulticallResponse({
      chainId: stakeNetwork.chainId,
      calls: [
        {
          target: StakeContract.address,
          callData: StakeContract.interface.encodeFunctionData('totalStakedSupply'),
          allowFailure: false
        },
        {
          target: StakeContract.address,
          callData: StakeContract.interface.encodeFunctionData('nextReleaseTimestamp'),
          allowFailure: false
        },
        ...(isDualAutostake(StakeContract)
          ? [
              {
                target: StakeContract.address,
                callData: StakeContract.interface.encodeFunctionData('claimStakedStartTimestamp'),
                allowFailure: false
              }
            ]
          : [
              {
                target: StakeContract.address,
                callData: StakeContract.interface.encodeFunctionData('claimRewardStartTimestamp'),
                allowFailure: false
              }
            ]),
        ...(isDualAutostake(StakeContract)
          ? [
              {
                target: StakeContract.address,
                callData: StakeContract.interface.encodeFunctionData('claimStakedPeriodDuration'),
                allowFailure: false
              }
            ]
          : [
              {
                target: StakeContract.address,
                callData: StakeContract.interface.encodeFunctionData('claimPeriodDuration'),
                allowFailure: false
              }
            ]),
        {
          target: StakeContract.address,
          callData: StakeContract.interface.encodeFunctionData('rewardUnlockPercentPerPeriod'),
          allowFailure: false
        }
      ]
    })

    const decodeOrReadBigNumber = async ({
      functionName,
      result,
      directRead
    }: {
      functionName:
        | 'totalStakedSupply'
        | 'nextReleaseTimestamp'
        | 'claimRewardStartTimestamp'
        | 'claimStakedStartTimestamp'
        | 'claimPeriodDuration'
        | 'claimStakedPeriodDuration'
        | 'rewardUnlockPercentPerPeriod'
      result?: { success: boolean; returnData: string }
      directRead: () => Promise<BigNumber>
    }): Promise<BigNumber> => {
      if (result?.success && result.returnData && result.returnData !== '0x') {
        try {
          // Typechain narrows decodeFunctionResult to one function name per
          // overload; this helper decodes by name for a whole family of reads,
          // which is a runtime operation the base Interface expresses correctly.
          const [decoded] = (StakeContract.interface as unknown as Interface).decodeFunctionResult(
            functionName,
            result.returnData
          )
          return decoded
        } catch (error) {
          logMessage(`Failed to decode ${functionName} from multicall for ${StakeContract.address}: ${error}`)
        }
      }

      const fallbackValue = await directRead()
      if (!fallbackValue) {
        throw new Error(`Failed to read ${functionName} for ${StakeContract.address}`)
      }
      return fallbackValue
    }

    const totalSupply = await decodeOrReadBigNumber({
      functionName: 'totalStakedSupply',
      result: totalStakedSupplyRes,
      directRead: async () => StakeContract.totalStakedSupply()
    })
    const nextReleaseTimestamp = await decodeOrReadBigNumber({
      functionName: 'nextReleaseTimestamp',
      result: nextReleaseTimestampRes,
      directRead: async () => StakeContract.nextReleaseTimestamp()
    })

    let claimRewardStartTimestamp: BigNumber
    let claimPeriodDuration: BigNumber

    if (isDualAutostake(StakeContract)) {
      claimRewardStartTimestamp = await decodeOrReadBigNumber({
        functionName: 'claimStakedStartTimestamp',
        result: claimRewardStartTimestampRes,
        directRead: async () => StakeContract.claimStakedStartTimestamp()
      })
    } else {
      claimRewardStartTimestamp = await decodeOrReadBigNumber({
        functionName: 'claimRewardStartTimestamp',
        result: claimRewardStartTimestampRes,
        directRead: async () => StakeContract.claimRewardStartTimestamp()
      })
    }

    if (isDualAutostake(StakeContract)) {
      claimPeriodDuration = await decodeOrReadBigNumber({
        functionName: 'claimStakedPeriodDuration',
        result: claimPeriodDurationRes,
        directRead: async () => StakeContract.claimStakedPeriodDuration()
      })
    } else {
      claimPeriodDuration = await decodeOrReadBigNumber({
        functionName: 'claimPeriodDuration',
        result: claimPeriodDurationRes,
        directRead: async () => StakeContract.claimPeriodDuration()
      })
    }

    const rewardUnlockPercentPerPeriod = await decodeOrReadBigNumber({
      functionName: 'rewardUnlockPercentPerPeriod',
      result: rewardUnlockPercentPerPeriodRes,
      directRead: async () => StakeContract.rewardUnlockPercentPerPeriod()
    })

    let nextToClaim = BigNumber.from(0)
    const currentTime = Math.floor(Date.now() / 1000)
    // Get current timestamp in seconds
    if (stakerInfo && currentTime - claimRewardStartTimestamp.toNumber() > 0) {
      const numberOfCycles =
        Math.ceil((currentTime - claimRewardStartTimestamp.toNumber()) / claimPeriodDuration.toNumber()) + 1
      // Calculate how much reward is unlocked
      const rewardUnlockPercent = rewardUnlockPercentPerPeriod.mul(BigNumber.from(numberOfCycles))

      if (isStakerDualAutostake(stakerInfo)) {
        // Calculate how much reward is claimable
        const claimableWithNextRelease = stakerInfo.staked.mul(rewardUnlockPercent).div(BigNumber.from(10000))
        // Calculate how much reward will be claimable with already claimed rewards
        nextToClaim = claimableWithNextRelease.sub(stakerInfo.claimedStaked)
      } else {
        // Calculate how much reward is claimable
        const claimableWithNextRelease = stakerInfo.balanceWithRewards
          .mul(rewardUnlockPercent)
          .div(BigNumber.from(10000))
        // Calculate how much reward will be claimable with already claimed rewards
        nextToClaim = claimableWithNextRelease.sub(stakerInfo.claimedBalance)
      }
    }

    let currentToClaim: BigNumber
    if (stakerInfo && isStakerDualAutostake(stakerInfo)) {
      currentToClaim = stakerInfo.reward.sub(stakerInfo.claimedReward).add(pendingReward || BigNumber.from(0))
    }

    const enableForRelease = await getEnableForRelease()
    const apy = await getAPYCalculation()
    const { chainId } = stakeNetwork

    const stakeConfig: StakeReward = {
      apy: Number(BNToString(apy)),
      fee: 0, // Hardcoded value since we don't have fee amount
      stakedAmount: stakerInfo ? stakerInfo.staked : null,
      earnedAmount: earnedAmount || null,
      totalStakedAmount: totalSupply || null,
      chainId
    }

    const claimConfig: StakeClaimReward = {
      totalToClaim: stakerInfo ? totalToClaim : null,
      nextToClaim,
      currentToClaim: enableForRelease || null,
      nextReleaseTimestamp: nextReleaseTimestamp || null
    }

    return {
      stakeConfig,
      claimConfig,
      ...(currentToClaim && { rewardConfig: { currentToClaim } })
    }
  }

  const getConfigWithoutAccount = async (): Promise<{ stakeConfig: StakeReward; claimConfig: StakeClaimReward }> => {
    const totalSupply = await getTotalStakedSupply()
    const nextReleaseTimestamp = await getStakeNextReleaseTimestamp()
    const nextReleaseAllocation = await getNextReleaseAllocation()
    const apy = await getAPYCalculation()
    const chainId = await getRewardStakeChainId()

    const stakeConfig: StakeReward = {
      apy: Number(BNToString(apy)),
      fee: 0, // Hardcoded value since we don't have fee amount
      stakedAmount: null,
      earnedAmount: null,
      totalStakedAmount: totalSupply || null,
      chainId
    }

    const claimConfig: StakeClaimReward = {
      totalToClaim: null,
      nextToClaim: nextReleaseAllocation || null,
      currentToClaim: null,
      nextReleaseTimestamp: nextReleaseTimestamp || null
    }

    return {
      stakeConfig,
      claimConfig
    }
  }

  return {
    getConfigWithAccount,
    getConfigWithoutAccount,
    claimStake,
    claimReward,
    getWalletStakeUpdates,
    getDeclarationEndTimestamp,
    getDeclarationStartTimestamp,
    getStakeEndTimestamp,
    getStakeDuration,
    getUpdateStakersEndTimestamp,
    getStakeStartTimestamp,
    getTotalStakedSupply
  }
}
